import {
  Alert,
  AlertIcon,
  AlertTitle,
  Button,
  Divider,
  FormControl,
  Icon,
  Link,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
} from '@chakra-ui/react';
import { DocumentTextIcon } from '@heroicons/react/outline';
import { yupResolver } from '@hookform/resolvers/yup';
import axios from 'axios';
import { ParseError } from 'papaparse';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslations } from 'use-intl';

import { Exceptions, FormErrors, noop } from '@blockpulse3/data/shared';
import {
  DropzoneFiles,
  DropzoneInput,
  ErrorMessage,
  ResponsiveModal,
  ResponsiveModalFooter,
  ResponsiveModalProps,
  useErrorToast,
  useSuccessToast,
  useWarningToast,
} from '@blockpulse3/ui/commons';

import { csvFileDefaultValue, csvFileSchema } from './schema';

type ParseErrorMessage = keyof Messages['CSVParseError'];

type Props = {
  /* ** Callback on modal close ** */
  onClose: () => void;
  /* ** Callback when modal submit is completed ** */
  onComplete?: () => void;
  fundraisingId: string;
  isCrowdfunding?: boolean;
} & Omit<ResponsiveModalProps, 'children'>;

export function FundraisingSubscribersCSVModal({
  onClose,
  onComplete = noop,
  fundraisingId,
  isCrowdfunding = false,
  ...props
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nSubscriberCSV = useTranslations('SubscribersCSV');
  const i18nCSVParseError = useTranslations('CSVParseError');
  const i18nFundraisingSubscriberForm = useTranslations('FundraisingSubscribersForm');

  const [files, setFiles] = useState<DropzoneFiles>({});
  const [isUploading, setIsUploading] = useState<boolean>(false);

  const errorToast = useErrorToast();
  const warningToast = useWarningToast();
  const successToast = useSuccessToast();

  const { control, handleSubmit, setValue, setError, clearErrors, formState, resetField } =
    useForm<{ csvFile: string }>({
      defaultValues: csvFileDefaultValue,
      resolver: yupResolver(csvFileSchema),
      mode: 'onChange',
    });

  const handleFileDrop = (files: File[]): void => {
    clearErrors('csvFile');
    setValue('csvFile', 'document.csv', { shouldValidate: true });
    setFiles({ [files[0].name]: files[0] });
  };

  const handleFileDelete = (): void => {
    clearErrors('csvFile');
    setValue('csvFile', '', { shouldValidate: true });
    setFiles({});
  };

  const handleFileError = (): void => {
    setError('csvFile', { message: FormErrors.InvalidFileFormat });
  };

  const handleModalClose = (): void => {
    resetField('csvFile');
    setFiles({});
    onClose();
  };

  const handleCSVSubmit = async (): Promise<void> => {
    setFiles({});
    const file = Object.values(files)[0];
    if (file && fundraisingId) {
      const formData = new FormData();
      formData.append('subscriptions', file);
      try {
        setIsUploading(true);
        const rep = await axios.post(
          process.env['NX_API_CONTROLLER_ENDPOINT'] +
            '/fundraisings/' +
            fundraisingId +
            '/importSubscriptions',
          formData,
          {
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data',
            },
          },
        );
        if (rep.data.length > 0) {
          successToast({ title: i18nFundraisingSubscriberForm('Toast.CSVSuccess') });
        } else {
          warningToast({ title: i18nFundraisingSubscriberForm('Toast.CSVWarning') });
        }
        setIsUploading(false);
      } catch (err: unknown) {
        if (axios.isAxiosError(err)) {
          const error = err?.response?.data as ParseError;
          if (!error.code && error.message === Exceptions.DisposableEmail) {
            errorToast({ title: i18nCSVParseError('DisposableEmail') });
          } else if (!error.code && error.message === Exceptions.MissingRequiredProperties) {
            errorToast({ title: i18nCSVParseError('MissingRequiredProperties') });
          } else {
            const code = error.code || 'DefaultError';
            errorToast({ title: i18nCSVParseError(code as ParseErrorMessage) });
          }
        }
        setIsUploading(false);
      }
    }
    handleModalClose();
    onComplete();
  };

  const csvTemplateLink = isCrowdfunding
    ? 'assets/blockpulse_template_equity_crowdfunding.csv'
    : 'assets/blockpulse_template_equity.csv';

  return (
    <ResponsiveModal onClose={handleModalClose} {...props}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18nSubscriberCSV('Title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing="4">
            <Alert status="info">
              <AlertIcon />
              <Stack spacing="2">
                <AlertTitle>{i18nSubscriberCSV('AlertTitle')}</AlertTitle>
                <AlertTitle>
                  <Link href={csvTemplateLink}>{i18nSubscriberCSV('AlertLink')}</Link>
                </AlertTitle>
              </Stack>
            </Alert>
            <form onSubmit={handleSubmit(handleCSVSubmit)}>
              <Controller
                control={control}
                name="csvFile"
                render={(): JSX.Element => (
                  <FormControl isInvalid={!!formState.errors?.csvFile}>
                    <DropzoneInput
                      accept={{ 'text/csv': [] }}
                      files={files}
                      icon={<Icon as={DocumentTextIcon} boxSize="42px" strokeWidth="1px" />}
                      subTitle="CSV de 5M"
                      onDelete={handleFileDelete}
                      onDrop={handleFileDrop}
                      onError={handleFileError}
                    />
                    <ErrorMessage error={formState.errors?.csvFile} />
                  </FormControl>
                )}
              />
            </form>
          </Stack>
        </ModalBody>
        <Divider />
        <ResponsiveModalFooter>
          <Button isDisabled={isUploading} variant="secondary" onClick={handleModalClose}>
            {i18nGeneral('Cancel')}
          </Button>
          <Button
            isDisabled={!formState.isValid || isUploading}
            isLoading={isUploading}
            variant="primary"
            onClick={handleCSVSubmit}
          >
            {i18nSubscriberCSV('ImportSubscribersDefault')}
          </Button>
        </ResponsiveModalFooter>
      </ModalContent>
    </ResponsiveModal>
  );
}

export type FundraisingSubscribersCSVModalProps = Props;
